<template>
	<v-card class="post-preview-card elevation-0 d-flex flex-column pt-7" v-if="this.post.user" style="padding-right: 0 !important; padding-left: 0 !important;">
		<div class="post-preview-card__container">
			<div class="post-preview-card__outer">
				<div class="d-flex post-preview-card__list pl-0 pr-0 pt-7">
					<div
						v-if="post.pin_to_top || post.sponsored_by"
						class="post-preview-card__pin"
						:class="
							pinnedType(post).icon === sponsoredStarIcon
								? 'post-preview-card__sponsored'
								: 'post-preview-card__pin'
						"
					>
						<span>
							<img :src="pinnedType(post).icon" alt="iconSponsored" class="mr-1" />
							{{ pinnedType(post).text }}
						</span>
					</div>

					<div class="post-preview-card__avatar-wrapper px-4">
						<router-link
							:to="{
								name: isCurrentUser(this.post.user.user_id) ? 'profile' : 'user-by-id',
								params: isCurrentUser(this.post.user.user_id) ? [] : {id: this.post.user.user_id}
							}"
							class="d-flex align-center text-decoration-none"
							:class="{ disabled: this.post.user.user_hidden }"
						>
							<Avatar
								class="mr-2 post-preview-card__avatar"
								size="50"
								:avatar="this.post.user.user_logo"
							/>
							<div
								class="d-flex flex-column justify-center gap-4 post-preview-card__meta"
							>
								{{ this.post.user.visible_username }}
								<span 
									v-if="this.post.user.second_profile_row"
									class="post-preview-card__meta-bottom" 
								>
									{{ this.post.user.second_profile_row }}
								</span>

							</div>
						</router-link>

						<div class="post-preview-card__meta-time">
							{{ dateCreated }}
						</div>
					</div>

					<div
						class="pa-0 pt-5 post-preview-card__thumbnail-outer"
						v-if="this.post.thread_image"
					>
						<img
							v-if="isTextPost || isPollPost"
							class="post-preview-card__thumbnail"
							:src="this.post.thread_image"
							alt="thread image"
							loading="lazy"
							@error="handleImageError"
						/>

						<!-- Vimeo video block  -->
						<div
							v-if="isPreviewVimeoModeActive"
							class='post-preview-card__thumbnail__vimeo'
						>
							<img
								class="post-preview-card__thumbnail"
								:src="this.post.thread_image"
								alt="thread image"
								loading="lazy"
								@error="handleImageError"
							/>
							<span
								@click='playVideo'
							></span>
						</div>
						<iframe
							v-if="!isPreviewVimeoModeActive && isVimeoVideo"
							:src="formateVimeoVideoLink"
							width="600"
							height="338"
							:ref="createUniqueIframeName(this.post)"
							allow="autoplay; encrypted-media"
							allowfullscreen
							@load='iframeLoaded'
						></iframe>

						<!-- Youtube video block  -->
						<div
							v-if="isPreviewYoutubeModeActive"
							class='post-preview-card__thumbnail__youtube'
						>
							<img
								class="post-preview-card__thumbnail"
								:src="this.post.thread_image"
								alt="thread image"
								loading="lazy"
								@error="handleImageError"
							/>
							<span
								@click='playVideo'
							></span>
						</div>
						<iframe
							v-if="!isPreviewYoutubeModeActive && isYoutubeVideo"
							:src="formateYoutubeVideoLink"
							width="600"
							height="338"
							:ref="createUniqueIframeName(this.post)"
							allow="autoplay; encrypted-media"
							@load='iframeLoaded'
							allowfullscreen
						></iframe>
					</div>

					<span class="px-4 pt-3 post-preview-card__link">
						<a :href="this.post.thread_link" target="_blank">
							{{ this.removeQueryParams(this.post.thread_link) }}
						</a>
					</span>

					<v-card-title class="post-preview-card__title pb-0 pt-5 px-4">
						<router-link
							:to="{
								name: 'posts-by-id',
								params: { id: this.post.thread_id },
								query: {
									enable_all_comments: enableAllComments ? 1 : 0
								}
							}"
						>
							{{ this.post.thread_title }}
						</router-link>
					</v-card-title>

					<v-card-text class="px-4 pb-3 post-preview-card__content">
						<div
							class="post-preview-card__content-inner"
							v-html="this.description"
						/>
					</v-card-text>

					<Poll v-if="isPollPost" :poll="post.poll" :postId="post.thread_id" />

					<div v-if="post.uploads && post.uploads.length">
						<div
							class="d-flex flex-column flex-wrap w-full px-4 pb-4 mb-n2"
							v-for="attachment in this.post.uploads"
							:key="attachment.upload_id"
						>
							<PostAttachments :attachment="attachment" />
						</div>
					</div>

					<v-card-subtitle
						class="post-preview-card__meta-bottom d-flex align-center px-4 pa-0 mb-4"
					>
						<div v-show="this.post.tags.length !== 0">
							<v-chip
								:color="tag.tag_bg_color"
								:text-color="tag.tag_txt_color"
								class="post-preview-card__tag mr-2 mb-2 px-3 py-3"
								v-for="(tag, index) in this.post.tags"
								:key="index"
							>
								#{{ tag.tag }}
							</v-chip>
						</div>
					</v-card-subtitle>

					<v-divider />

					<div
						class="d-flex align-center justify-space-between mt-4 mb-4 px-4"
					>
						<div class="d-flex">
							<v-btn 
								class="post-preview-card__like-btn elevation-0 pa-0" 
								@click="onLike"
							>
								<v-icon
									class="mr-2"
									style="cursor: pointer"
									size="medium"
									:class="
										isLiked
											? 'post-preview-card__like-counter-active'
											: 'post-preview-card__like-counter'
									"
								>
									mdi-thumb-up
								</v-icon>
								<span
									:class="
										isLiked
											? 'post-preview-card__like-counter-active'
											: 'post-preview-card__like-counter'
									"
								>
									{{ this.likes }}
								</span>
							</v-btn>
							<v-btn
								v-if="enableAllComments && allowComments"
								class="post-preview-card__like-btn elevation-0 pa-0" 
								@click="$router.push({
									name: 'posts-by-id',
									params: { id: post.thread_id },
									query: {
										enable_all_comments: enableAllComments ? 1 : 0
									}
								})"
							>
									<v-icon
										style="cursor: pointer"
										size="medium"
										class="post-preview-card__like-counter mr-2"
									>
										mdi-comment
									</v-icon>
									<span class="post-preview-card__like-counter">
										{{ this.commentsCount }}
									</span>
							</v-btn>

							<div class="d-flex align-center justify-center" style="width: 64px">
								<v-icon class="post-preview-card__like-counter mr-2" size="medium">
									mdi-eye
								</v-icon>
								<span class="post-preview-card__like-counter">
									{{ this.post.view_count }}
								</span>
							</div>
						</div>

						<div 
							class="d-flex justify-end" 
							@click="onFollow" 
							style="cursor: pointer"
						>
							<span class="post-preview-card__like-button d-flex align-center">
								<v-icon class="mr-2" :style="{color: this.isFollowed ? '#a0a0a0' : 'var(--accent-color)'}">
									{{ this.isFollowed ? 'mdi-bookmark' : 'mdi-bookmark-outline' }}
								</v-icon>
								<span
									class="post-preview-card__book-button"
									:class="
										this.isFollowed
											? 'post-preview-card__book-button-active'
											: ''
									"
								>
									{{ isFollowed ? 'Bookmarked' : 'Bookmark' }}
								</span>
							</span>
						</div>
					</div>

					<v-divider />

					<div
						class="d-flex align-center px-4"
						v-if="allowComments && enableAllComments"
					>
						<div style="width: 100%" v-if="this.isAuthenticated">
							<CommentForm
								class="mb-5 pl-0"
								:avatar="this.profile.user_logo"
								:parent="this.post"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	</v-card>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import { toggleDislike, toggleFollow, toggleLike, toggleUnFollow } from '@/api/post'
import { mapActions, mapGetters } from 'vuex'
import Avatar from '@/components/Avatar'
import pinnedIcon from '@/assets/images/icon-pinned.svg'
import sponsoredStarIcon from '@/assets/images/icoт-sponsored-star.svg'
import PostAttachments from '@/components/Post/PostAttachments'
import CommentForm from '@/components/Forms/CommentForm.vue'
import Poll from '@/components/Post/Poll.vue'

Vue.use(VueScrollTo)

const THREAD_VIDEO_TYPE = 3;
const VIDEO_TYPE_YOUTUBE = 1;
const VIDEO_TYPE_VIMEO = 2;
const THREAD_POLL_TYPE = 4;

export default {
	components: {
		CommentForm,
		Avatar,
		PostAttachments,
		Poll
	},
	props: {
		post: {
			type: Object,
			default: () => {},
		},
	},
	data() {
		return {
			offset: true,
			isLiked: false,
			isFollowed: false,
			likes: 0,
			commentsCount: 0,
			enableAllComments: false,
			notAvailableImage: require('@/assets/images/image_not_available.png'),

			previewMode: true,
			iframeElement: null,

			pinnedIcon,
			sponsoredStarIcon,
			description: this.post.thread_text,
			avatar: 'https://e7.pngegg.com/pngimages/799/987/png-clipart-computer-icons-avatar-icon-design-avatar-heroes-computer-wallpaper-thumbnail.png',
			titleImage:
				'https://images.familyhomeplans.com/cdn-cgi/image/fit=contain,quality=100/plans/44207/44207-b1200.jpg',
		}
	},
	computed: {
		...mapGetters('auth', ['isAuthenticated', 'profile']),
		...mapGetters('auth', ['profile', 'isAuthenticated']),
		...mapGetters('settings', ['appCore']),

		whichView() {
			return this.view
		},
		dateCreated() {
			const date = new Date(this.post.publish_timestamp * 1000)
			const options = { year: 'numeric', month: 'long', day: 'numeric' }
			return date.toLocaleDateString('en-US', options)
		},
		allowComments() {
			return !!this.post.allow_comments;
		},
		formateVimeoVideoLink() {
			const {
				thread_video_code: code
			} = this.post || {}

			const additionalParams = '?autoplay=1&mute=1'

			if (this.isVimeoVideoWithPreviewImage) {
				return `https://player.vimeo.com/video/${code}${additionalParams}`
			}

			return `https://player.vimeo.com/video/${code}`
		},
		formateYoutubeVideoLink() {
			const {thread_video_code: code} = this.post || {}

			const additionalParams = '?autoplay=1&mute=1'

			if (this.isYoutubeVideoWithPreviewImage) {
				return `https://www.youtube.com/embed/${code}${additionalParams}`
			}

			return `https://www.youtube.com/embed/${code}`
		},
		isYoutubeVideoWithPreviewImage() {
			const {
				thread_type: type,
				thread_image: image,
				thread_video_type: videoType
			} = this.post || {}

			return type === THREAD_VIDEO_TYPE &&
				videoType === VIDEO_TYPE_YOUTUBE &&
				image
		},
		isVimeoVideoWithPreviewImage() {
			const {
				thread_type: type,
				thread_image: image,
				thread_video_type: videoType
			} = this.post || {}

			return type === THREAD_VIDEO_TYPE &&
				videoType === VIDEO_TYPE_VIMEO &&
				image
		},
		isVimeoVideo() {
			const {
				thread_type: type,
				thread_video_type: videoType
			} = this.post || {}

			return type === THREAD_VIDEO_TYPE &&
				videoType === VIDEO_TYPE_VIMEO
		},
		isYoutubeVideo() {
			const {
				thread_type: type,
				thread_video_type: videoType
			} = this.post || {}

			return type === THREAD_VIDEO_TYPE &&
				videoType === VIDEO_TYPE_YOUTUBE

		},
		isPreviewVimeoModeActive() {
			if (this.isVimeoVideoWithPreviewImage) {
				if (this.previewMode) {
					return true;
				}
			}

			return false;
		},
		isPreviewYoutubeModeActive() {
			if (this.isYoutubeVideoWithPreviewImage) {
				if (this.previewMode) {
					return true;
				}
			}

			return false;
		},

		isTextPost() {
			return this.post.thread_type !== THREAD_VIDEO_TYPE
		},

		isPollPost() {
			return this.post.thread_type === THREAD_POLL_TYPE
		}
	},
	methods: {
		...mapActions('post', ['getPost', 'deletePost']),
		navigateToComments() {
			if (!this.post.id) {
				return
			}

			this.$router.push({ name: 'SinglePost', params: { id: this.post.id } })
			setTimeout(() => {
				VueScrollTo.scrollTo('#comment-id', 1000)
			}, 500)
		},
		isCurrentUser(userId) {
			if (!this.profile) {
				return false
			}

			const {user_id: currentUserId} = this.profile
			return userId === currentUserId;
		},
		handleImageError() {
			this.post.thread_image = this.notAvailableImage;
		},
		createUniqueIframeName(post) {
			const {thread_video_code: videoCode} = post || {}

			return `videoIframe_${videoCode}`
		},
		playVideo() {
			this.previewMode = false;
		},
		iframeLoaded() {
			const {thread_video_code: code} = this.post || {}

			this.iframeElement =  this.$refs[`videoIframe_${code}`];
		},
		removeQueryParams(url) {
			if (!url) {
				return ''
			}

			let parsedUrl = new URL(url);
			parsedUrl.search = '';

			return parsedUrl.toString().slice(0, 65)
		},
		onLike() {
			if (this.isLiked) {
				if (this.likes === 0) {
					return
				}
				this.likes--
				toggleDislike(this.post.thread_id)
			} else if (!this.isLiked) {
				this.likes++
				toggleLike({ thread_id: this.post.thread_id })
			}

			this.isLiked = !this.isLiked
		},
		onFollow() {
			if (this.isFollowed) {
				toggleUnFollow(this.post.thread_id)
			} else if (!this.isFollowed) {
				toggleFollow({ thread_id: this.post.thread_id })
			}

			this.isFollowed = !this.isFollowed
		},
		onPostEdit(post) {
			if (!post.id) {
				return
			}
			this.$router.push(`/post/${post.id}/edit`)
		},
		onPostDelete(post) {
			if (!post.id) {
				return
			}

			this.deletePost(post.id)
		},
		pinnedType(post) {
			let result = {}

			if (post.pin_to_top && post.sponsored_by) {
				return (result = { icon: sponsoredStarIcon, text: 'Sponsored by ' + post.sponsored_by })
			} else if (post.sponsored_by) {
				return (result = { icon: sponsoredStarIcon, text: 'Sponsored by ' + post.sponsored_by })
			} else if (post.pin_to_top) {
				result = { icon: pinnedIcon, text: 'Pinned' }
			} else {
				result = { icon: pinnedIcon, text: 'Pinned' }
			}

			return result
		},
	},

	created() {
		// parse html text to  save spaces and rows
		const parser = new DOMParser()
		const doc = parser.parseFromString(this.post.thread_text, 'text/html')
		this.description = doc.body.innerHTML

		if (this.post) {
			this.likes = this.post.upvote_count
			this.commentsCount = this.post.comments_count
			this.isLiked = this.post.upvote
			this.isFollowed = this.post.follow
		}

		if (+this.$route.query.enable_all_comments) {
			this.enableAllComments = true
		}
	},
}
</script>

<style lang="scss">
.post-preview-card {
	background: #f1f1f1 !important;

	&__container {
		width: 100%;
		height: 100%;
		border-radius: 10px !important;
		box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.06);
		background: white !important;
	}

	&__content-inner {
		overflow-x: auto;
		color: rgb(74, 74, 74);
		font-size: 15px;
		font-family: Montserrat, sans-serif;
		font-weight: normal;
		line-height: 18px;
		padding-top: 10px;
		overflow-x: auto;

		img {
			width: 100%;
		}

		span {
			white-space: normal;
		}

		p {
			margin-bottom: 0;
		}

		h1 {
			font-size: 30px;
			padding-bottom: 8px;
			line-height: 30px;
		}

		a {
			color: var(--ceil-detail-text-color) !important;
		}
	}

	&__meta {
		font-family: Montserrat, sans-serif;
		font-style: normal;
		font-weight: bold;
		font-size: 18px;
		line-height: 20px;
		color: var(--username-category-title);
		letter-spacing: 0.36px;
		gap: 4px;
		word-break: break-word;

		&-bottom {
			color: var(--company-position-large);
			font-size: 16px;
			font-family: Montserrat, sans-serif;
			font-weight: normal;
			line-height: 16px;
		}

		&-time {
			color: rgb(155, 155, 155);
			font-size: 12px;
			font-family: Montserrat, sans-serif;
			font-weight: normal;
			text-align: right;

			@media (max-width: 480px) {
				text-align: left;
				padding-left: 58px;
			}
		}
	}
	&__avatar {
		img {
			object-fit: cover;
		}
		&-wrapper {
			display: flex;
			justify-content: space-between;
			align-items: center;

			@media (max-width: 480px) {
				flex-direction: column;
				align-items: flex-start;
			}
		}
	}

	&__outer {
	}

	&__list {
		position: relative;
		flex-direction: column;
	}

	&__link {
		color: var(--ceil-detail-text-color);
		font-size: 16px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
		text-decoration: underline;
		cursor: pointer;

		a {
			color: var(--ceil-detail-text-color) !important;
		}
	}
	&__title {
		color: rgb(74, 74, 74);
		font-size: 18px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
		letter-spacing: 0px;
		line-height: 22px;
		font-style: normal;

		a {
			color: #202232 !important;
			text-decoration: none;
		}
	}

	&__block-view {
		flex-direction: column;
		height: 73%;
		.post-preview-card__thumbnail-outer img {
			max-width: 100%;
		}
	}
	&__content {
		font-family: Montserrat, sans-serif;
		font-style: normal;
		font-weight: 500;
		font-size: 16px;
		line-height: 21px;
	}

	&__thumbnail {
		height: auto;
		width: 100%;

		&-outer iframe {
			width: 100% !important;
		}
	}

	&__tag {
		padding: 6px;
		border-radius: 3px !important;
		color: rgb(74, 74, 74);
		font-size: 14px !important;
		font-family: Montserrat, sans-serif;
		font-weight: normal;
		height: 20px !important;
		cursor: pointer;
		span {
			min-width: 100% !important;
			width: 100%;
		}
	}

	&__like-btn {
		background-color: transparent !important;
	}

	&__like-counter {
		color: var(--accent-color) !important;
		font-size: 14px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;

		&-active {
			font-size: 14px;
			font-family: Montserrat, sans-serif;
			font-weight: bold;
			color: rgb(160, 160, 160) !important;
		}
	}

	&__share-button {
		font-family: Montserrat, sans-serif;
		font-style: normal;
		font-weight: 500;
		font-size: 13px;
		line-height: 16px;
		color: #aaaaaa !important;
		cursor: pointer;

		.v-icon {
			transform: rotateY(180deg);
		}

		&:hover {
			span {
				color: #757575;
				text-decoration: underline;
			}
		}
	}

	&__book-button {
		color: var(--accent-color);
		font-size: 14px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
		text-decoration: none;

		@media (max-width: 360px) {
			display: none;
		}

		&-active {
			color: #a0a0a0 !important;
			text-decoration: underline;
		}
	}

	&__comment {
		color: rgb(74, 74, 74);
		font-size: 15px;
		font-family: Montserrat, sans-serif;
		font-weight: normal;
		letter-spacing: -0.43px;
		width: 100%;

		&::placeholder {
			color: rgb(160, 160, 160);
			font-size: 15px;
			font-family: Montserrat, sans-serif;
			font-weight: normal;
			letter-spacing: -0.43px;
		}
		&:focus {
			outline: none;
		}
		&-send {
			width: 38px;
			height: 22px;
			color: rgb(160, 160, 160);
			font-size: 14px;
			font-family: Montserrat, sans-serif;
			font-weight: bold;
			text-decoration: underline;
			cursor: pointer;
		}
	}

	&__comments-button {
		cursor: pointer;
		&:hover {
			span {
				text-decoration: underline;
			}
		}
	}

	&__pin {
		text-align: right;
		position: absolute;
		top: 0;
		right: 0;

		span {
			z-index: 0;
			position: relative;
			background: var(--badge-pin-background);
			display: inline-flex;
			padding: 5px 10px 5px 10px;
			border-top-right-radius: 8px;
			color: rgb(255, 255, 255);
			font-size: 12px;
			font-family: Montserrat, sans-serif;
			font-weight: bold;
		}

		// Start
		span {
			&:before {
				z-index: -1;
				content: '';
				position: absolute;
				left: -8%;
				top: 0;
				height: 100%;
				width: 100%;
				background: var(--badge-pin-background);
				-webkit-transform: skewX(20deg);
				-moz-transform: skewX(20deg);
				-ms-transform: skewX(20deg);
				transform: skewX(20deg);
			}
		}
	}
	&__sponsored {
		text-align: right;
		position: absolute;
		top: 0;
		right: 0;

		span {
			z-index: 0;
			position: relative;
			background: var(--badge-sponsored-background);
			display: inline-flex;
			padding: 5px 10px 5px 10px;
			border-top-right-radius: 8px;
			color: rgb(255, 255, 255);
			font-size: 12px;
			font-family: Montserrat, sans-serif;
			font-weight: bold;
		}

		// Start
		span {
			&:before {
				z-index: -1;
				content: '';
				position: absolute;
				left: -8%;
				top: 0;
				height: 100%;
				width: 100%;
				background: var(--badge-sponsored-background);
				-webkit-transform: skewX(20deg);
				-moz-transform: skewX(20deg);
				-ms-transform: skewX(20deg);
				transform: skewX(20deg);
			}
		}
	}
	&__my-menu {
		margin-top: 20px;
		min-width: 175px !important;
		contain: initial !important;
		overflow: visible !important;
		&:before {
			content: '';
			position: absolute;
			left: 14px;
			top: 0;
			z-index: -1;
			border-radius: 3px;
			transform: translate(0, -50%) rotate(45deg);
			width: 20px;
			height: 20px;
			background: #fff;
			box-shadow: 0 0 11px rgb(0 0 0 / 14%);
		}
	}
	&__menu-dots {
		margin-left: 10px !important;
	}

	@media screen and (max-width: 1904px) {
		&__block {
			flex: 0 0 100%;
		}
		&__list {
			flex: 0 0 75%;
		}
	}
	@media screen and (max-width: 1264px) {
		&__block {
			flex: 0 0 100%;
		}
		&__list {
			flex: 0 0 66%;
		}
	}

	@media screen and (min-width: 1264px) {
		&__container {
			max-width: 600px;
		}
	}
	@media screen and (max-width: 768px) {
		&__thumbnail {
			height: 100%;
			max-width: 100%;
		}
		&__default-thumbnail {
			max-height: 250px;
			height: 100%;
		}
		&__outer {
			flex-direction: column;
		}
	}
}
.dropdown-profile {
	padding: 15px;

	&__item {
		min-height: 30px !important;
		padding: 0;
	}

	&__title {
		color: #aaaaaa;
		font-size: 12px !important;
		line-height: 1.4 !important;
		font-family: 'Montserrat', sans-serif !important;
		font-weight: 600 !important;
	}

	&__title:hover {
		text-decoration: underline;
		color: black;
		transition: color 0.2s, background-color 0.2s, border-color 0.2s !important;
	}
}
.post-preview-card__thumbnail__youtube,
.post-preview-card__thumbnail__vimeo {
	position: relative;
}

.post-preview-card__thumbnail__youtube span,
.post-preview-card__thumbnail__vimeo span {
	position: absolute;
	top: 50%;
	left: 50%;

	width: 0;
	height: 0;
	border-style: solid;
	border-width: 20px 0 20px 30px;
	border-color: transparent transparent transparent #b3b2b2;
	cursor: pointer;
	transition: border-color 0.2s ease-in-out, border-width 0.2s ease-in-out;

	transform: translate(-50%, -50%);
}
.post-preview-card__thumbnail__youtube span:hover,
.post-preview-card__thumbnail__vimeo span:hover {
	border-color: transparent transparent transparent #D8D5D5FF;
	border-width: 30px 0 30px 40px;
}
iframe {
	border: none;
}
.disabled {
	pointer-events: none;
}
</style>
